/***
 *
 *   TABLE
 *   Please view the full documentation: https://docs.usegravity.app/ui/table
 *
 *   PROPS
 *   data: array of objects for body and header (optional)
 *   search: bool to show the search field
 *   sort: allow the table columns to be sorted
 *   loading: bool to toggle loading spinner
 *   badge - object containing column name and color to add badges to column
 *   show - array of columns (object key names) to show (shows all if not provided)
 *   header - array of objects with name and title keys to define the table header
 *   hide - array of columns (object key names) to hide
 *   actions: object with edit/delete keys set to callback functions (optional)
 *   scrollable: bool to enable horizontal scroll with ALL scrollbars hidden (clean UI)
 *   truncateColumns: array of column names that should show truncated text with tooltip
 *   truncateLength: number of characters to show before truncating (default: 50)
 *   mobileHiddenColumns: array of column names to hide on mobile devices
 *   tabletHiddenColumns: array of column names to hide on tablet devices
 *
 ***********/

import React, { Fragment, useState, useEffect } from "react";
import { Loader, Search, ClassHelper } from "components/lib";
import { Header } from "./header";
import { Body } from "./body";
import { ScrollIndicator } from "./ScrollIndicator";
import Style from "./table.tailwind.js";

export function Table(props) {
  // state
  const [header, setHeader] = useState(null);
  const [body, setBody] = useState(null);
  const [filter, setFilter] = useState(false);

  useEffect(() => {
    if (props.data) {
      // create the headers
      let header = props.header || [];

      if (!header.length) {
        for (let key in props.data[0]) {
          header.push({
            name: key,
            title: key.replace("_", " "),
            sort: key === "actions" ? false : true,
          });
        }
      }

      setBody(props.data);
      setHeader(header);
    }
  }, [props.data, props.header]);

  // loading
  if (props.loading) {
    return (
      <div className={Style.loading}>
        <Loader />
      </div>
    );
  }

  // no data
  if (!header && !body) return false;

  function sort(column, direction) {
    const rows = filter.length ? [...filter] : [...body];

    rows.sort(function (a, b) {
      if (a[column] && b[column]) {
        a[column].badge ? (a = a[column].label) : (a = a[column]);

        b[column].badge ? (b = b[column].label) : (b = b[column]);

        if (direction === "desc") {
          if (a > b) return -1;
          if (a < b) return 1;
          else return 0;
        } else {
          if (a < b) return -1;
          if (a > b) return 1;
          else return 0;
        }
      } else {
        return false;
      }
    });

    filter ? setFilter(rows) : setBody(rows);
  }

  function search(term) {
    // search each cell in each row &
    // update state to show only filtered rows
    let rowsToShow = [];

    body.forEach((row) => {
      for (let cell in row) {
        // Asegúrate de que el valor sea un string, si es nulo o indefinido convierte a string vacío
        const cellValue = row[cell] != null ? row[cell].toString() : "";

        if (cellValue.toLowerCase().includes(term.toLowerCase())) {
          if (!rowsToShow.includes(row)) rowsToShow.push(row);
        }
      }
    });

    setFilter(rowsToShow);
  }

  function editRowCallback(res, row) {
    let state = [...body];
    let stateRow = state[state.findIndex((x) => x.id === row.id)];
    Object.keys(res).map((key) => (stateRow[key] = res[key].value));
    setBody(state);
  }

  function deleteRowCallback(res, row) {
    let state = [...body];
    state.splice(
      state.findIndex((x) => x.id === row.id),
      1
    );
    setBody(state);
  }

  const tableStyle = ClassHelper(Style, {
    table: true,
  });

  return (
    <Fragment>
      {props.search && (
        <Search
          className={Style.search}
          callback={search}
          throttle={props.throttle}
        />
      )}

      <ScrollIndicator className={props.scrollable ? Style.scrollContainer : ""}>
        <table className={!props.naked && tableStyle}>
          {header && (
            <Header
              data={header}
              callback={sort}
              show={props.show}
              hide={props.hide}
              actions={props.actions}
              mobileHiddenColumns={props.mobileHiddenColumns}
              tabletHiddenColumns={props.tabletHiddenColumns}
            />
          )}
          {body && (
            <Body
              data={filter ? filter : body}
              show={props.show}
              hide={props.hide}
              badge={props.badge}
              truncateColumns={props.truncateColumns}
              truncateLength={props.truncateLength}
              mobileHiddenColumns={props.mobileHiddenColumns}
              tabletHiddenColumns={props.tabletHiddenColumns}
              actions={{
                edit: props.actions?.edit,
                view: props.actions?.view,
                delete: props.actions?.delete,
                email: props.actions?.email,
                custom: props.actions?.custom,
              }}
              callback={{
                edit: editRowCallback,
                delete: deleteRowCallback,
              }}
            />
          )}
        </table>
      </ScrollIndicator>
    </Fragment>
  );
}
